import React, { useEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";
import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.register(ChartDataLabels);

export default function GraphDisplay({ data }) {
  const chartRef = useRef(null);
  const [chartInstance, setChartInstance] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState(null);

  const categories = {
    Financeiro: ["FATURAMENTO", "CUSTO", "META", "TOTAL", "ICMS", "TRANSF"],
    Operacional: ["ENTREGAS", "VOLUMES", "PESO"],
    Custos: ["CUSTO TRANSF KG", "CUSTO COLETA KG", "CUSTO ENTREGA KG"],
    Outros: ["PORCENTAGEM", "DIFERENCA"],
  };

  useEffect(() => {
    if (!data || data.length === 0 || !selectedMetric) return;

    if (chartInstance) {
      chartInstance.destroy();
    }

    if (chartRef.current) {
      const ctx = chartRef.current.getContext("2d");

      const newChartInstance = new Chart(ctx, {
        type: "bar",
        data: {
          labels: data.map((d) => d.BASE),
          datasets: [
            {
              label: selectedMetric,
              data: data.map((d) => {
                const value = d[selectedMetric];
                if (typeof value === 'string') {
                  return parseFloat(value.replace(/[^\d,.-]/g, "").replace(",", ".")) || 0;
                }
                return value || 0;
              }),
              backgroundColor: "rgba(54, 162, 235, 0.5)",
              borderColor: "rgba(54, 162, 235, 1)",
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            datalabels: {
              anchor: 'end',
              align: 'end',
              formatter: (value) => value.toFixed(2),
              color: 'black',
              font: {
                weight: 'bold'
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
            },
          },
        },
      });

      setChartInstance(newChartInstance);
    }
  }, [data, selectedMetric]);

  useEffect(() => {
    if (data && data.length > 0) {
      setSelectedCategory("Financeiro");
      setSelectedMetric("FATURAMENTO");
    }
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p>Selecione ao menos uma base</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 h-screen bg-white-less p-4">
      <div className="flex flex-col items-center justify-center rounded-lg  p-4 mb-6 gap-6">
        <div className="flex flex-row items-center justify-center bg-white-whiter rounded-lg shadow-lg p-4 mb-6 gap-6">
          <h3 className="text-lg font-bold">Gráfico Comparativo</h3>
          <div className="flex flex-wrap gap-4">
            {Object.keys(categories).map((category) => (
              <button
                key={category}
                onClick={() =>
                  setSelectedCategory(
                    selectedCategory === category ? null : category
                  )
                }
                className={`px-4 py-2 rounded-lg border ${
                  selectedCategory === category
                    ? "bg-salBlue text-white"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
        
        {selectedCategory && (
          <div className="flex flex-col items-center bg-white-whiter rounded-lg shadow-lg p-4 mb-6 gap-4">
            <h4 className="font-semibold text-sm">Selecione uma métrica em "{selectedCategory}"</h4>
            <div className="flex flex-wrap gap-2 justify-center">
              {categories[selectedCategory].map((metric) => (
                <button
                  key={metric}
                  onClick={() => setSelectedMetric(metric)}
                  className={`px-4 py-2 rounded-lg border ${
                    selectedMetric === metric
                      ? "bg-green-500 text-white"
                      : "bg-gray-200 hover:bg-gray-300"
                  }`}
                >
                  {metric}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="w-full max-w-2xl h-80 bg-white-whiter rounded-lg shadow-lg p-4">
          <canvas ref={chartRef} />
        </div>
      </div>

      <div className="flex flex-col items-center justify-center bg-white-whiter rounded-lg shadow-lg p-4 mb-6 gap-6 overflow-y-auto">
        <h3 className="text-lg font-bold">Bases Selecionadas</h3>
        <div className="w-full max-w-2xl bg-white-whiter rounded-lg p-4 mt-4 overflow-y-auto">
          <table className="min-w-full bg-white-whiter">
            <thead>
              <tr>
                <th className="py-3 px-6 border-b">Base</th>
                <th className="py-3 px-6 border-b">Informações</th>
              </tr>
            </thead>
            <tbody>
              {data.map((base) => (
                <tr key={base.BASE}>
                  <td className="border px-4 py-2 font-semibold">{base.BASE}</td>
                  <td className="border px-4 py-2">
                    <div className="text-left">
                      {Object.entries(base).map(([key, value]) => (
                        <p key={key} className="mb-1"><strong>{key}:</strong> {value}</p>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
